import { type ReactElement, useId, useState } from 'react';
import { AddDialog } from '@/components/AddDialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { getMessage } from '@/shared/copy/messages';
import { formErrorAlertSx } from '@/shared/ui/formAlertStyles';
import { s } from '@/theme/tokens';
import { uiToneSeverityMap } from '@/theme/uiTokens';
import { Box, Stack, Text } from '@/ui';
import { VisibilityIcon, VisibilityOffIcon } from '@/ui/primitives';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Select,
  TextField,
} from '@/ui/primitives';
import { InputAdornment, MenuItem } from '@/ui/primitives';
import type { OrganisationRole, UserListItemDto } from '@flowqa/shared';

type ManageUserDialogProps = {
  open: boolean;
  user: UserListItemDto | null;
  roleValue: OrganisationRole;
  saving?: boolean;
  removeDisabled?: boolean;
  onRoleChange: (value: OrganisationRole) => void;
  onClose: () => void;
  onUpdate: () => void;
  onRemove: () => void;
};

export const ManageUserDialog = ({
  open,
  user,
  roleValue,
  saving = false,
  removeDisabled = false,
  onRoleChange,
  onClose,
  onUpdate,
  onRemove,
}: ManageUserDialogProps): ReactElement => {
  const dialogPaddingTop = s('sm');
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Text role="overline" component="span">
          {user ? `Manage ${user.email}` : 'Manage User'}
        </Text>
      </DialogTitle>
      <DialogContent sx={{ pt: dialogPaddingTop }}>
        {user ? (
          <Stack gap="md">
            <Select
              label="Role"
              value={roleValue}
              onChange={(event) => {
                onRoleChange(event.target.value as OrganisationRole);
              }}
              fullWidth
            >
              <MenuItem value="ADMIN">Admin</MenuItem>
              <MenuItem value="MEMBER">User</MenuItem>
            </Select>
            <Button
              variant="secondary"
              onClick={onRemove}
              disabled={removeDisabled}
              sx={{ color: 'error.main', borderColor: 'error.main' }}
            >
              Remove From Organisation
            </Button>
          </Stack>
        ) : (
          <Text role="body" color="textSecondary">
            Select a user to manage.
          </Text>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} variant="secondary">
          Cancel
        </Button>
        <Button onClick={onUpdate} variant="primary" disabled={saving}>
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type RemoveUserDialogProps = {
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  confirmDisabled?: boolean;
};

export const RemoveUserDialog = ({
  open,
  loading = false,
  onClose,
  onConfirm,
  confirmDisabled = false,
}: RemoveUserDialogProps): ReactElement => {
  return (
    <ConfirmDialog
      open={open}
      title="Remove User"
      description="This user will be removed from the organisation."
      confirmLabel="Delete Account"
      confirmColor="error"
      loading={loading}
      confirmDisabled={confirmDisabled}
      onClose={onClose}
      onConfirm={onConfirm}
    />
  );
};

type InviteUserDialogProps = {
  open: boolean;
  email: string;
  saving?: boolean;
  disableInvite?: boolean;
  errorMessage?: string | null;
  onEmailChange: (value: string) => void;
  onClose: () => void;
  onInvite: () => void;
};

export const InviteUserDialog = ({
  open,
  email,
  saving = false,
  disableInvite = false,
  errorMessage,
  onEmailChange,
  onClose,
  onInvite,
}: InviteUserDialogProps): ReactElement => {
  return (
    <AddDialog
      open={open}
      title="Invite User"
      saveLabel="Send Invite"
      saving={saving}
      disableSave={disableInvite}
      errorMessage={errorMessage}
      onClose={onClose}
      onSave={onInvite}
    >
      <TextField
        label="Email Address"
        type="email"
        value={email}
        onChange={(event) => {
          onEmailChange(event.target.value);
        }}
        fullWidth
      />
    </AddDialog>
  );
};

type WithdrawInviteDialogProps = {
  open: boolean;
  loading?: boolean;
  confirmDisabled?: boolean;
  email: string | null;
  onClose: () => void;
  onConfirm: () => void;
};

export const WithdrawInviteDialog = ({
  open,
  loading = false,
  confirmDisabled = false,
  email,
  onClose,
  onConfirm,
}: WithdrawInviteDialogProps): ReactElement => {
  return (
    <ConfirmDialog
      open={open}
      title="Cancel Invite"
      description={email ? `Cancel the pending invite for ${email}?` : 'Cancel the pending invite?'}
      confirmLabel="Cancel Invite"
      confirmColor="red"
      loading={loading}
      confirmDisabled={confirmDisabled}
      onClose={onClose}
      onConfirm={onConfirm}
    />
  );
};

type ReinviteUserDialogProps = {
  open: boolean;
  loading?: boolean;
  confirmDisabled?: boolean;
  email: string | null;
  onClose: () => void;
  onConfirm: () => void;
};

export const ReinviteUserDialog = ({
  open,
  loading = false,
  confirmDisabled = false,
  email,
  onClose,
  onConfirm,
}: ReinviteUserDialogProps): ReactElement => {
  return (
    <ConfirmDialog
      open={open}
      title="Reinvite User"
      description={email ? `Send a new invite to ${email}?` : 'Send a new invite?'}
      confirmLabel="Send Invite"
      confirmColor="primary"
      loading={loading}
      confirmDisabled={confirmDisabled}
      onClose={onClose}
      onConfirm={onConfirm}
    />
  );
};

type DeleteAccountDialogProps = {
  open: boolean;
  password: string;
  passwordErrorMessage?: string;
  confirmDisabled?: boolean;
  loading?: boolean;
  onPasswordChange: (value: string) => void;
  onClose: () => void;
  onConfirm: () => void;
};

const passwordIconButtonSx = { '&:hover': { backgroundColor: 'transparent' } } as const;

export const DeleteAccountDialog = ({
  open,
  password,
  passwordErrorMessage,
  confirmDisabled = false,
  loading = false,
  onPasswordChange,
  onClose,
  onConfirm,
}: DeleteAccountDialogProps): ReactElement => {
  const passwordId = useId();
  const [showPassword, setShowPassword] = useState(false);
  const PasswordField = TextField;

  return (
    <ConfirmDialog
      open={open}
      title={getMessage('account.delete.dialogTitle')}
      confirmLabel={getMessage('account.delete.confirmLabel')}
      confirmColor="error"
      loading={loading}
      confirmDisabled={confirmDisabled}
      onClose={onClose}
      onConfirm={onConfirm}
      description={
        <Stack gap="md">
          <Text role="body" color="textSecondary">
            {getMessage('account.delete.dialogDescription')}
          </Text>
          <Box>
            <PasswordField
              id={passwordId}
              label={getMessage('account.delete.passwordLabel')}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(event) => {
                onPasswordChange(event.target.value);
              }}
              fullWidth
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        onClick={() => {
                          setShowPassword((current) => !current);
                        }}
                        edge="end"
                        size="small"
                        sx={passwordIconButtonSx}
                      >
                        {showPassword ? (
                          <VisibilityOffIcon fontSize="small" />
                        ) : (
                          <VisibilityIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
          {passwordErrorMessage ? (
            <Alert
              severity={uiToneSeverityMap.red}
              color="error"
              variant="outlined"
              sx={formErrorAlertSx}
            >
              {passwordErrorMessage}
            </Alert>
          ) : null}
        </Stack>
      }
    />
  );
};
